import { createProgram, uniformLoc } from "./gl";
import type { Mat4 } from "./math";
import particleVert from "../shaders/particle.vert?raw";
import particleFrag from "../shaders/particle.frag?raw";
import type { ParticleSimulation } from "./particles/simulate";
import {
  PARTICLE_INSTANCE_FLOATS,
  PARTICLE_MAX_INSTANCES,
} from "./particles/types";

function normalize3(x: number, y: number, z: number): [number, number, number] {
  const l = Math.hypot(x, y, z);
  if (l < 1e-8) return [1, 0, 0];
  return [x / l, y / l, z / l];
}

/** Строки view (rotation): правый и верхний вектор камеры в мире (billboard). */
function cameraAxesFromView(view: Mat4): {
  camRight: [number, number, number];
  camUp: [number, number, number];
} {
  const rx = view[0];
  const ry = view[4];
  const rz = view[8];
  const ux = view[1];
  const uy = view[5];
  const uz = view[9];
  return {
    camRight: normalize3(rx, ry, rz),
    camUp: normalize3(ux, uy, uz),
  };
}

/** Простой RGBA-атлас 4×4 для flipbook (CPU); без внешнего PNG. */
function buildProceduralParticleAtlasRgba(size = 512): Uint8Array {
  const out = new Uint8Array(size * size * 4);
  const gw = 4;
  const gh = 4;
  const cw = size / gw;
  const ch = size / gh;
  for (let py = 0; py < size; py++) {
    for (let px = 0; px < size; px++) {
      const gx = Math.min(gw - 1, Math.floor(px / cw));
      const gy = Math.min(gh - 1, Math.floor(py / ch));
      const lx = (px - gx * cw) / cw - 0.5;
      const ly = (py - gy * ch) / ch - 0.5;
      const dist = Math.sqrt(lx * lx + ly * ly);
      const blob = Math.max(0, 1 - dist * 2.45);
      const cell = gx + gy * gw;
      const flick = 0.72 + 0.28 * Math.sin(px * 0.08 + py * 0.06 + cell * 2.1);
      const a = Math.min(255, blob * flick * 280);
      const i = (py * size + px) * 4;
      if (cell < 8) {
        out[i] = 255;
        out[i + 1] = 110 + (cell % 3) * 20;
        out[i + 2] = 35;
      } else {
        out[i] = 170 + (cell % 5) * 10;
        out[i + 1] = 175 + (cell % 5) * 8;
        out[i + 2] = 182;
      }
      out[i + 3] = Math.floor(a);
    }
  }
  return out;
}

export class ParticleRenderer {
  private readonly gl: WebGL2RenderingContext;
  private readonly prog: WebGLProgram;
  private readonly vao: WebGLVertexArrayObject;
  private readonly instanceBuf: WebGLBuffer;
  private readonly fireData: Float32Array;
  private readonly smokeData: Float32Array;
  private readonly dummyDepthTex: WebGLTexture;
  private readonly atlasTex: WebGLTexture;

  private readonly uViewProj: WebGLUniformLocation | null;
  private readonly uView: WebGLUniformLocation | null;
  private readonly uCamRight: WebGLUniformLocation | null;
  private readonly uCamUp: WebGLUniformLocation | null;
  private readonly uCamForward: WebGLUniformLocation | null;
  private readonly uTime: WebGLUniformLocation | null;
  private readonly uInvResolution: WebGLUniformLocation | null;
  private readonly uDepthTex: WebGLUniformLocation | null;
  private readonly uAtlasTex: WebGLUniformLocation | null;
  private readonly uUseAtlas: WebGLUniformLocation | null;
  private readonly uAtlasGrid: WebGLUniformLocation | null;
  private readonly uAtlasFramesFire: WebGLUniformLocation | null;
  private readonly uAtlasFramesSmoke: WebGLUniformLocation | null;
  private readonly uAtlasSmokeBase: WebGLUniformLocation | null;
  private readonly uSoftParticles: WebGLUniformLocation | null;
  private readonly uNear: WebGLUniformLocation | null;
  private readonly uFar: WebGLUniformLocation | null;

  private _useAtlas = true;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    this.prog = createProgram(gl, particleVert, particleFrag);
    this.uViewProj = uniformLoc(gl, this.prog, "uViewProj");
    this.uView = uniformLoc(gl, this.prog, "uView");
    this.uCamRight = uniformLoc(gl, this.prog, "uCamRight");
    this.uCamUp = uniformLoc(gl, this.prog, "uCamUp");
    this.uCamForward = uniformLoc(gl, this.prog, "uCamForward");
    this.uTime = uniformLoc(gl, this.prog, "uTime");
    this.uInvResolution = uniformLoc(gl, this.prog, "uInvResolution");
    this.uDepthTex = uniformLoc(gl, this.prog, "uDepthTex");
    this.uAtlasTex = uniformLoc(gl, this.prog, "uAtlasTex");
    this.uUseAtlas = uniformLoc(gl, this.prog, "uUseAtlas");
    this.uAtlasGrid = uniformLoc(gl, this.prog, "uAtlasGrid");
    this.uAtlasFramesFire = uniformLoc(gl, this.prog, "uAtlasFramesFire");
    this.uAtlasFramesSmoke = uniformLoc(gl, this.prog, "uAtlasFramesSmoke");
    this.uAtlasSmokeBase = uniformLoc(gl, this.prog, "uAtlasSmokeBase");
    this.uSoftParticles = uniformLoc(gl, this.prog, "uSoftParticles");
    this.uNear = uniformLoc(gl, this.prog, "uNear");
    this.uFar = uniformLoc(gl, this.prog, "uFar");

    const nFloats = PARTICLE_MAX_INSTANCES * PARTICLE_INSTANCE_FLOATS;
    this.fireData = new Float32Array(nFloats);
    this.smokeData = new Float32Array(nFloats);

    const dtex = gl.createTexture();
    if (!dtex) throw new Error("particle dummy depth tex");
    gl.bindTexture(gl.TEXTURE_2D, dtex);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.DEPTH_COMPONENT24,
      1,
      1,
      0,
      gl.DEPTH_COMPONENT,
      gl.UNSIGNED_INT,
      null,
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);
    this.dummyDepthTex = dtex;

    const at = gl.createTexture();
    if (!at) throw new Error("particle atlas tex");
    const rgba = buildProceduralParticleAtlasRgba(512);
    gl.bindTexture(gl.TEXTURE_2D, at);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 512, 512, 0, gl.RGBA, gl.UNSIGNED_BYTE, rgba);
    gl.bindTexture(gl.TEXTURE_2D, null);
    this.atlasTex = at;

    const ib = gl.createBuffer();
    if (!ib) throw new Error("particle instance buf");
    this.instanceBuf = ib;

    const vao = gl.createVertexArray();
    if (!vao) throw new Error("particle VAO");
    this.vao = vao;
    gl.bindVertexArray(vao);

    const cornerBuf = gl.createBuffer();
    if (!cornerBuf) throw new Error("particle corner buf");
    gl.bindBuffer(gl.ARRAY_BUFFER, cornerBuf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([
        -1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1,
      ]),
      gl.STATIC_DRAW,
    );
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.vertexAttribDivisor(0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuf);
    gl.bufferData(gl.ARRAY_BUFFER, nFloats * 4, gl.DYNAMIC_DRAW);
    const stride = PARTICLE_INSTANCE_FLOATS * 4;
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 4, gl.FLOAT, false, stride, 0);
    gl.vertexAttribDivisor(1, 1);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 4, gl.FLOAT, false, stride, 16);
    gl.vertexAttribDivisor(2, 1);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.FLOAT, false, stride, 32);
    gl.vertexAttribDivisor(3, 1);
    gl.enableVertexAttribArray(4);
    gl.vertexAttribPointer(4, 4, gl.FLOAT, false, stride, 48);
    gl.vertexAttribDivisor(4, 1);

    gl.bindVertexArray(null);
  }

  setUseAtlas(on: boolean): void {
    this._useAtlas = on;
  }

  draw(params: {
    viewProj: Mat4;
    view: Mat4;
    time: number;
    viewportW: number;
    viewportH: number;
    sim: ParticleSimulation;
    eyeX: number;
    eyeY: number;
    eyeZ: number;
    camForwardX: number;
    camForwardY: number;
    camForwardZ: number;
    depthTex: WebGLTexture | null;
    softParticles: boolean;
    near: number;
    far: number;
  }): void {
    const gl = this.gl;
    const { fireCount, smokeCount } = params.sim.packInstances(
      params.time,
      params.eyeX,
      params.eyeY,
      params.eyeZ,
      this.fireData,
      this.smokeData,
    );

    if (fireCount === 0 && smokeCount === 0) return;

    const { camRight, camUp } = cameraAxesFromView(params.view);
    const cf = normalize3(params.camForwardX, params.camForwardY, params.camForwardZ);

    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.depthMask(false);

    gl.useProgram(this.prog);
    gl.bindVertexArray(this.vao);

    if (this.uViewProj) gl.uniformMatrix4fv(this.uViewProj, false, params.viewProj);
    if (this.uView) gl.uniformMatrix4fv(this.uView, false, params.view);
    if (this.uCamRight) gl.uniform3f(this.uCamRight, camRight[0], camRight[1], camRight[2]);
    if (this.uCamUp) gl.uniform3f(this.uCamUp, camUp[0], camUp[1], camUp[2]);
    if (this.uCamForward) gl.uniform3f(this.uCamForward, cf[0], cf[1], cf[2]);
    if (this.uTime) gl.uniform1f(this.uTime, params.time);
    if (this.uInvResolution) {
      gl.uniform2f(
        this.uInvResolution,
        1 / Math.max(1, params.viewportW),
        1 / Math.max(1, params.viewportH),
      );
    }
    if (this.uNear) gl.uniform1f(this.uNear, params.near);
    if (this.uFar) gl.uniform1f(this.uFar, params.far);

    const dtex = params.depthTex ?? this.dummyDepthTex;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, dtex);
    if (this.uDepthTex) gl.uniform1i(this.uDepthTex, 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.atlasTex);
    if (this.uAtlasTex) gl.uniform1i(this.uAtlasTex, 1);
    if (this.uUseAtlas) gl.uniform1f(this.uUseAtlas, this._useAtlas ? 1 : 0);
    if (this.uAtlasGrid) gl.uniform2f(this.uAtlasGrid, 4, 4);
    if (this.uAtlasFramesFire) gl.uniform1f(this.uAtlasFramesFire, 8);
    if (this.uAtlasFramesSmoke) gl.uniform1f(this.uAtlasFramesSmoke, 8);
    if (this.uAtlasSmokeBase) gl.uniform1f(this.uAtlasSmokeBase, 8);

    const soft = params.softParticles && params.depthTex !== null ? 1 : 0;
    if (this.uSoftParticles) gl.uniform1f(this.uSoftParticles, soft);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuf);

    if (fireCount > 0) {
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.fireData.subarray(0, fireCount * PARTICLE_INSTANCE_FLOATS));
      gl.enable(gl.BLEND);
      gl.blendEquation(gl.FUNC_ADD);
      gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE, gl.ONE, gl.ONE);
      gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, fireCount);
    }

    if (smokeCount > 0) {
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.smokeData.subarray(0, smokeCount * PARTICLE_INSTANCE_FLOATS));
      gl.enable(gl.BLEND);
      gl.blendEquation(gl.FUNC_ADD);
      gl.blendFuncSeparate(
        gl.SRC_ALPHA,
        gl.ONE_MINUS_SRC_ALPHA,
        gl.ONE,
        gl.ONE_MINUS_SRC_ALPHA,
      );
      gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, smokeCount);
    }

    gl.depthMask(true);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, null);
  }
}
